import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import type { DocumentPrefillNavState } from "@/pages/NewSubmissionPage";
import { useAuth } from "@/auth/AuthProvider";
import { SubmissionFlowProgress } from "@/components/SubmissionFlowProgress";
import { ManifestDetailsForm } from "@/components/ManifestDetailsForm";
import { QuoteComparison } from "@/components/QuoteComparison";
import { AccountForm } from "@/components/AccountForm";
import { deriveFlowStep } from "@/lib/submissionFlow";
import {
  getAccount,
  updateAccount,
  accountMeetsFloor,
  unmetAccountFields,
  type AccountDetail,
  type AccountFloorField,
} from "@/lib/brokerAccounts";
import {
  getSubmission,
  listSubmissionQuotes,
  setSubmissionDetails,
  type BrokerQuote,
  type SubmissionDetail,
} from "@/lib/brokerSubmissions";
import {
  getProducts,
  accountFloorFields,
  type MissingField,
  type ProductManifest,
} from "@/lib/brokerProducts";
import { messageFor } from "@/lib/errors";

// Broker submission detail + quoting view (BRK-S040, unified in BRK-E003). Shows
// the submission header + the flow stepper, then — once quote-ready — the
// QuoteComparison table (BRK-S049) which auto-pulls the price indication and
// drives the firm-quote flow. Before quote-readiness it shows the MANIFEST-
// DRIVEN details step (BRK-S052 — the product's indication floor off the
// catalog manifest, prefilled from the submission's field bag) or, for an
// incomplete insured, the insured-profile form inline (BRK-S046 gate →
// BRK-S050 inline form). Every not-ready state is handled, never a
// white-screen (NN #7).
// The detail page collapses the six flow steps into the three panels it can
// show (BRK-S060). "profile" = the insured-profile step (2), "rating" = the
// manifest details step (3), "pricing" = the quoting view (steps 4-6).
type FlowStage = "profile" | "rating" | "pricing";
const STAGE_LABEL: Record<FlowStage, string> = {
  profile: "Insured profile",
  rating: "Rating details",
  pricing: "Pricing & quotes",
};
const STAGE_EDIT_LABEL: Record<FlowStage, string> = {
  profile: "Edit insured profile",
  rating: "Edit rating details",
  pricing: "Pricing & quotes",
};
function stageOfStep(step: number): FlowStage {
  return step <= 2 ? "profile" : step === 3 ? "rating" : "pricing";
}

export function SubmissionDetailPage() {
  const { id = "" } = useParams();
  const { authedRequest } = useAuth();
  // The document-intake prefill handed over from New Business (BRK-S038), never
  // persisted (no auto-commit): the details form marks it for review, and the
  // Save button is the explicit confirmation. Absent on every other entry path.
  const location = useLocation();
  const documentPrefill = (location.state as DocumentPrefillNavState | null)
    ?.documentPrefill;

  const [detail, setDetail] = useState<SubmissionDetail | null>(null);
  const [quotes, setQuotes] = useState<BrokerQuote[]>([]);
  // The insured account, read to gate the rating screen on an incomplete floor
  // (BRK-S046). Best-effort: a failed read leaves the gate open.
  const [account, setAccount] = useState<AccountDetail | null>(null);
  // The product's manifest, resolved from the catalog by the submission's
  // product id (BRK-S052): undefined = still loading, null = no manifest
  // (a legacy product with no online rating).
  const [manifest, setManifest] = useState<ProductManifest | null | undefined>(
    undefined,
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // The step the broker is VIEWING (BRK-S060), an ephemeral, session-scoped
  // choice — never persisted (NN #9): null follows the server-derived stage,
  // a value re-opens an already-completed step to review/edit. The derived
  // stage stays the source of truth for what's done; this only picks the panel.
  const [view, setView] = useState<FlowStage | null>(null);
  // The quote-stage profile-completion form's open state + a ref to scroll to
  // it, so the firm-quote gate (BRK-S057) can route the broker straight into it.
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement | null>(null);
  const openProfile = useCallback(() => {
    setProfileOpen(true);
    requestAnimationFrame(() =>
      profileRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      }),
    );
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [d, q] = await Promise.all([
        getSubmission(authedRequest, id),
        listSubmissionQuotes(authedRequest, id),
      ]);
      setDetail(d);
      setQuotes(q.items);
      // Read the insured account to know whether its required floor is met, so
      // the rating screen can gate a draft (BRK-S046). A failed read is
      // non-fatal — the gate defaults open (see accountComplete below).
      if (d.account?.id) {
        try {
          setAccount(await getAccount(authedRequest, d.account.id));
        } catch {
          setAccount(null);
        }
      }
      // Resolve the product manifest that drives the details step (BRK-S052).
      // A failed catalog read degrades to the explicit not-available state.
      try {
        const { items } = await getProducts(authedRequest);
        setManifest(
          items.find((p) => p.id === d.product?.id)?.manifest ?? null,
        );
      } catch {
        setManifest(null);
      }
    } catch (err) {
      setLoadError(messageFor(err));
    } finally {
      setLoading(false);
    }
  }, [authedRequest, id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Refresh just the persisted quotes (no full-page spinner) — QuoteComparison
  // calls this after auto-pulling an indication or requesting a firm quote.
  const refreshQuotes = useCallback(async () => {
    try {
      const { items } = await listSubmissionQuotes(authedRequest, id);
      setQuotes(items);
    } catch {
      // A failed background refresh of the list is non-fatal.
    }
  }, [authedRequest, id]);

  if (loading) {
    return (
      <section className="mx-auto w-full max-w-3xl py-8">
        <p role="status" className="text-sm text-brk-dim">
          Loading submission…
        </p>
      </section>
    );
  }

  if (loadError || !detail) {
    return (
      <section className="mx-auto flex w-full max-w-3xl flex-col gap-4 py-8">
        <p role="alert" className="text-sm text-brk-danger">
          {loadError ?? "Submission not found."}
        </p>
        <Link
          to="/submissions"
          className="text-sm text-brk-accent hover:underline"
        >
          ← Back to submissions
        </Link>
      </section>
    );
  }

  // A submission is "rated" (quote-ready) once it has an effective date. Until
  // then, capture the manifest details instead of the pricing sections (BRK-S052).
  const rated = Boolean(detail.effectiveDate);

  // The STAGE-AWARE account floor (BRK-S057): the account-scope fields the
  // manifest requires at each stage. Step 2 needs only the INDICATION account
  // floor (address for WC — no email/phone/personnel); the QUOTE floor (what a
  // carrier needs to bind) is collected later, at the firm-quote step. Defaults
  // open when the account or manifest couldn't be read, so a fetch failure never
  // blocks a legitimate submission (NN #7); the server stays the authority.
  const indicationAccountFloor: AccountFloorField[] = manifest
    ? accountFloorFields(manifest, "indication")
    : [];
  const quoteAccountFloor: AccountFloorField[] = manifest
    ? accountFloorFields(manifest, "quote")
    : [];
  const accountComplete =
    account && manifest
      ? accountMeetsFloor(indicationAccountFloor, account)
      : true;
  // The account-scope QUOTE-floor fields still unmet (BRK-S057) — drives the
  // firm-quote gate + the completion affordance. Account-scoped, so tagged as
  // such for the shared checklist rendering.
  const quoteFloorMissing: MissingField[] =
    account && manifest
      ? unmetAccountFields(quoteAccountFloor, account).map((f) => ({
          key: f.key,
          label: f.label,
          scope: "account" as const,
        }))
      : [];

  // An incomplete insured (a draft that can't reach an indication yet) → capture
  // just the indication account floor inline, right here in the flow (BRK-S050 →
  // BRK-S057). Narrowed to non-null so the form gets a defined account.
  const incompleteAccount =
    !rated && account && manifest && !accountComplete ? account : null;

  // The unified-flow step, derived purely from server state (BRK-S045, NN #9).
  const flowStep = deriveFlowStep({
    accountComplete,
    rated,
    hasIndications: quotes.some((q) => q.kind === "indication"),
    hasQuotes: quotes.some((q) => q.kind === "quote"),
  });

  // The step this page shows (BRK-S060). The DERIVED stage is the live position;
  // `completedStages` are the earlier steps that are now done (and so
  // revisitable); `activeStage` is what's on screen — the derived stage unless
  // the broker chose to re-open a completed one (bidirectional: back to a done
  // step, and forward again to where they left off). The derivation stays the
  // source of truth; `view` is ephemeral UI state only.
  const derivedStage = stageOfStep(flowStep);
  const completedStages: FlowStage[] = [
    ...(flowStep >= 3 ? (["profile"] as const) : []),
    ...(flowStep >= 4 ? (["rating"] as const) : []),
  ];
  const reachable = new Set<FlowStage>([...completedStages, derivedStage]);
  const activeStage: FlowStage =
    view && reachable.has(view) ? view : derivedStage;
  const revisiting = activeStage !== derivedStage;

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-6 py-8">
      <SubmissionFlowProgress current={flowStep} />
      <div className="flex flex-col gap-1">
        <h1 className="font-mono text-2xl font-bold tracking-tight">
          {detail.submissionNumber}
        </h1>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-brk-dim">
          <StatusBadge status={detail.status} />
          {detail.product && <span>{detail.product.name}</span>}
          {detail.account && (
            <Link
              to={`/accounts/${detail.account.id}`}
              className="text-brk-accent hover:underline"
            >
              {detail.account.legalName}
            </Link>
          )}
          {detail.effectiveDate && (
            <span>
              Effective {detail.effectiveDate}
              {detail.expirationDate ? ` – ${detail.expirationDate}` : ""}
            </span>
          )}
        </div>
      </div>

      {/* Revisiting a completed step: an explicit way back forward to the live
          stage (BRK-S060). */}
      {revisiting && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-brk-border bg-brk-bg px-4 py-3 text-sm"
        >
          <span className="text-brk-dim">
            Reviewing a completed step —{" "}
            <span className="font-medium text-brk-text">
              {STAGE_LABEL[activeStage]}
            </span>
            . Saving returns you to where you left off.
          </span>
          <button
            type="button"
            onClick={() => setView(null)}
            className="shrink-0 font-medium text-brk-accent hover:underline"
          >
            Back to {STAGE_LABEL[derivedStage]} →
          </button>
        </div>
      )}

      {/* On the live stage, offer to re-open any already-completed step
          (BRK-S060). Not-yet-reachable steps simply aren't listed. */}
      {!revisiting && completedStages.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span className="text-brk-dim">Review a completed step:</span>
          {completedStages.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setView(s)}
              className="font-medium text-brk-accent hover:underline"
            >
              {STAGE_EDIT_LABEL[s]}
            </button>
          ))}
        </div>
      )}

      {activeStage === "profile" ? (
        incompleteAccount ? (
          <InsuredProfileStep
            account={incompleteAccount}
            floorFieldKeys={indicationAccountFloor.map((f) => f.key)}
            onSaved={setAccount}
          />
        ) : account ? (
          // Revisiting a completed insured profile (BRK-S060): the full editable
          // form, saved through the same PUT seam; on save the derived stage
          // recomputes and we snap back to the live stage.
          <ProfileReview
            account={account}
            floorFieldKeys={indicationAccountFloor.map((f) => f.key)}
            onSaved={(fresh) => {
              setAccount(fresh);
              setView(null);
            }}
          />
        ) : (
          <p role="status" className="text-sm text-brk-dim">
            Loading insured profile…
          </p>
        )
      ) : activeStage === "rating" ? (
        manifest === undefined ? (
          <p role="status" className="text-sm text-brk-dim">
            Loading rating details…
          </p>
        ) : manifest === null ? (
          // A product with no registered manifest (legacy single-carrier GL):
          // there's no online details capture — handled, not an error (NN #7).
          <div className="rounded-md border border-brk-border bg-brk-bg px-3 py-2 text-sm text-brk-dim">
            Online rating isn’t available for this product yet — our team will
            follow up on this submission.
          </div>
        ) : (
          <>
            {!revisiting && detail.carriedForwardKeys.length > 0 && account && (
              // The renewal account-review moment (BRK-S055): carried account
              // info gets the same review opportunity as the carried bag.
              <RenewalAccountReview account={account} onSaved={setAccount} />
            )}
            <ManifestDetailsForm
              manifest={manifest}
              submissionId={detail.id}
              // The saved effective date lands on the typed spine column, so
              // merge it back into the bag when re-opening a rated submission —
              // it must not read as blank (BRK-S060). On the document path
              // (BRK-S038) the extracted bag prefills UNDER the server's data
              // (anything already saved wins) and is marked for review; skipped
              // when revisiting a completed step.
              initialBag={{
                ...(!revisiting ? (documentPrefill?.bag ?? {}) : {}),
                ...detail.data,
                ...(detail.effectiveDate
                  ? { effectiveDate: detail.effectiveDate }
                  : {}),
              }}
              carriedKeys={revisiting ? [] : detail.carriedForwardKeys}
              extractedKeys={revisiting ? [] : documentPrefill?.extractedKeys}
              unmatchedCodes={revisiting ? [] : documentPrefill?.unmatchedCodes}
              // Resolved class-code refs (PMS-S150) so carried-forward AND
              // document-extracted exposures prefill with the code + description,
              // not the canonical uuid (BRK-S062 / BRK-S038). The server's own
              // refs win; the document proposal's refs fill in the extracted bag
              // (not yet persisted, so not in dataDisplayRefs).
              codeRefs={{
                ...(!revisiting ? (documentPrefill?.codeRefs ?? {}) : {}),
                ...detail.dataDisplayRefs,
              }}
              onSubmit={async (bag) => {
                setDetail(await setSubmissionDetails(authedRequest, id, bag));
                setView(null);
              }}
            />
          </>
        )
      ) : (
        <>
          {account && manifest && quoteFloorMissing.length > 0 && (
            // The quote-stage account floor (BRK-S057): fields a carrier needs
            // to firm up a bindable quote (e.g. entity type / contact email for
            // PIE) that weren't required to price. Collected here — honestly
            // tied to when carriers need them — not front-loaded onto step 2.
            // The firm-quote gate below opens/scrolls this via `openProfile`.
            <div ref={profileRef}>
              <QuoteProfileCompletion
                account={account}
                floorFieldKeys={quoteAccountFloor.map((f) => f.key)}
                open={profileOpen}
                onOpenChange={setProfileOpen}
                onSaved={setAccount}
              />
            </div>
          )}
          <QuoteComparison
            submissionId={id}
            quotes={quotes}
            onQuotesChanged={refreshQuotes}
            quoteFloorMissing={quoteFloorMissing}
            onCompleteProfile={openProfile}
          />
        </>
      )}

      <Link
        to="/submissions"
        className="text-sm text-brk-accent hover:underline"
      >
        ← Back to submissions
      </Link>
    </section>
  );
}

// The renewal account-review affordance (BRK-S055): the insured profile was
// carried from the prior term, so it gets a review OFFER — collapsed by
// default (the floor is met; this is not a gate), expanding to the same shared
// AccountForm used everywhere else (one capture surface, no duplicate form).
function RenewalAccountReview({
  account,
  onSaved,
}: {
  account: AccountDetail;
  onSaved: (fresh: AccountDetail) => void;
}) {
  const { identity, authedRequest } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  if (confirmed) {
    return (
      <p role="status" className="text-sm text-brk-dim">
        Insured profile reviewed.
      </p>
    );
  }
  if (!open) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-brk-border bg-brk-panel p-4">
        <div className="flex min-w-0 flex-col">
          <span className="text-sm font-medium text-brk-text">
            Insured profile carried from the prior term
          </span>
          <span className="text-sm text-brk-dim">
            Worth a quick look before quoting — update anything that changed.
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="shrink-0 rounded-md border border-brk-border px-3 py-1.5 text-sm font-medium text-brk-accent transition-colors hover:bg-brk-bg"
        >
          Review insured profile
        </button>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="font-semibold text-brk-text">
          Review the insured profile
        </h2>
        <p className="text-sm text-brk-dim">
          Carried from the prior term — confirm or update, then save.
        </p>
      </div>
      <AccountForm
        initial={account}
        withServicingBroker
        initialServicingBrokerId={
          account.servicingBroker?.id ?? identity?.brokerId ?? null
        }
        submitLabel="Save profile"
        pendingLabel="Saving…"
        onSubmit={async (input) => {
          onSaved(await updateAccount(authedRequest, account.id, input));
          setConfirmed(true);
        }}
        cancelSlot={
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-sm text-brk-accent hover:underline"
          >
            Close without saving
          </button>
        }
      />
    </div>
  );
}

// The quote-stage profile-completion affordance (BRK-S057). At the quote stage
// the insured needs the fields a carrier requires to bind (e.g. email, owners)
// that weren't needed to price. Collapsed by default (not a hard gate on the
// indication that's already showing), expanding to the shared AccountForm
// scoped to just the quote-stage account floor — one capture surface, honestly
// timed to when carriers actually need the detail.
function QuoteProfileCompletion({
  account,
  floorFieldKeys,
  open,
  onOpenChange,
  onSaved,
}: {
  account: AccountDetail;
  floorFieldKeys: string[];
  /** Controlled by the page so the firm-quote gate can open + scroll it. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (fresh: AccountDetail) => void;
}) {
  const { identity, authedRequest } = useAuth();

  if (!open) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-brk-border bg-brk-panel p-4">
        <div className="flex min-w-0 flex-col">
          <span className="text-sm font-medium text-brk-text">
            A firm quote needs a little more about the insured
          </span>
          <span className="text-sm text-brk-dim">
            Add it now to request a bindable quote — indications above don’t
            need it.
          </span>
        </div>
        <button
          type="button"
          onClick={() => onOpenChange(true)}
          className="shrink-0 rounded-md border border-brk-border px-3 py-1.5 text-sm font-medium text-brk-accent transition-colors hover:bg-brk-bg"
        >
          Complete the insured profile
        </button>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="font-semibold text-brk-text">
          Complete the insured profile for a firm quote
        </h2>
        <p className="text-sm text-brk-dim">
          Carriers need these to bind — save, then request a firm quote.
        </p>
      </div>
      <AccountForm
        initial={account}
        withServicingBroker
        floorFieldKeys={floorFieldKeys}
        initialServicingBrokerId={
          account.servicingBroker?.id ?? identity?.brokerId ?? null
        }
        submitLabel="Save profile"
        pendingLabel="Saving…"
        onSubmit={async (input) => {
          onSaved(await updateAccount(authedRequest, account.id, input));
        }}
        cancelSlot={
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-sm text-brk-accent hover:underline"
          >
            Not now
          </button>
        }
      />
    </div>
  );
}

// The account-floor step (BRK-S046 gate → BRK-S050 inline form). A submission on
// a draft insured whose required floor isn't met can't be rated or priced. Rather
// than a gate that punts the broker to the read-only account page, we render the
// editable insured-profile form INLINE, right here in the flow (step 2): the
// broker completes it in place and the page advances to rating. It's the same
// shared AccountForm as New Business / account edit (one capture surface), with
// the servicing broker kept/defaulted to the logged-in broker. Placed on the
// detail so it protects every entry path (linear flow, a resumed draft, a
// bookmarked URL), consistent with the derived-from-server-state stepper.
function InsuredProfileStep({
  account,
  floorFieldKeys,
  onSaved,
}: {
  account: AccountDetail;
  floorFieldKeys: string[];
  onSaved: (fresh: AccountDetail) => void;
}) {
  const { identity, authedRequest } = useAuth();
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="font-semibold text-brk-text">
          Complete the insured profile
        </h2>
        <p className="text-sm text-brk-dim">
          Just the insured’s address to get an indication — add anything else
          later.
        </p>
      </div>
      <AccountForm
        initial={account}
        withServicingBroker
        floorFieldKeys={floorFieldKeys}
        initialServicingBrokerId={
          account.servicingBroker?.id ?? identity?.brokerId ?? null
        }
        submitLabel="Save & continue"
        pendingLabel="Saving…"
        onSubmit={async (input) => {
          onSaved(await updateAccount(authedRequest, account.id, input));
        }}
      />
    </div>
  );
}

// Revisiting a completed insured profile (BRK-S060): the shared AccountForm in
// the same stage-floor mode as the initial capture (so it never demands fields
// a minimum-information account never collected), saved through the same PUT
// seam. On save the parent snaps back to the live stage.
function ProfileReview({
  account,
  floorFieldKeys,
  onSaved,
}: {
  account: AccountDetail;
  floorFieldKeys: string[];
  onSaved: (fresh: AccountDetail) => void;
}) {
  const { identity, authedRequest } = useAuth();
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="font-semibold text-brk-text">Insured profile</h2>
        <p className="text-sm text-brk-dim">
          Review or update the insured, then save.
        </p>
      </div>
      <AccountForm
        initial={account}
        withServicingBroker
        floorFieldKeys={floorFieldKeys}
        initialServicingBrokerId={
          account.servicingBroker?.id ?? identity?.brokerId ?? null
        }
        submitLabel="Save profile"
        pendingLabel="Saving…"
        onSubmit={async (input) => {
          onSaved(await updateAccount(authedRequest, account.id, input));
        }}
      />
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span className="rounded-full border border-brk-border bg-brk-bg px-2 py-0.5 text-xs font-medium text-brk-text">
      {statusLabel(status)}
    </span>
  );
}

function statusLabel(status: string): string {
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
